import { useState, useEffect, useRef } from 'react';
import { useOrder } from '../../context/OrderContext';
import { DrainageSelector, StepTypeSelector } from '../../selectors';

const DrainageProcess = ({ onComplete, isEditMode = false, editItemIndex = null }) => {
    const { state, dispatch } = useOrder();

    // Add a ref to track if articles have already been added (prevents duplicates)
    const articlesAddedRef = useRef(false);

    // Check if we're in bar supply mode
    const isBarSupply = state.orderDetails.processing?.supplyType === 'barre';
    const currentBarLength = state.orderDetails.processing?.currentBar?.barLength;
    const barGroups = state.orderDetails.processing?.barGroups;

    const calcStandardHolesTotal = () => {
        if (barGroups && barGroups.length > 0) {
            return barGroups.reduce((sum, g) => {
                const hpb = g.barLength === 3 ? 2 : g.barLength === 6 ? 3 : 1;
                return sum + hpb * g.quantity;
            }, 0);
        }
        const hpb = currentBarLength === 3 ? 2 : currentBarLength === 6 ? 3 : 1;
        const nb = state.orderDetails.processing?.currentBar?.quantity || 1;
        return hpb * nb;
    };

    const calcStandardFresatureTotal = () => {
        if (barGroups && barGroups.length > 0) {
            return barGroups.reduce((sum, g) => {
                const fpb = g.barLength === 3 ? 5 : g.barLength === 6 ? 10 : 1;
                return sum + fpb * g.quantity;
            }, 0);
        }
        const fpb = currentBarLength === 3 ? 5 : currentBarLength === 6 ? 10 : 1;
        const nb = state.orderDetails.processing?.currentBar?.quantity || 1;
        return fpb * nb;
    };

    const calcCustomTotal = (valuePerBar) => {
        if (barGroups && barGroups.length > 0) {
            return barGroups.reduce((sum, g) => sum + valuePerBar * g.quantity, 0);
        }
        const nb = state.orderDetails.processing?.currentBar?.quantity || 1;
        return valuePerBar * nb;
    };

    const getTotalBars = () => {
        if (barGroups && barGroups.length > 0) {
            return barGroups.reduce((sum, g) => sum + g.quantity, 0);
        }
        return state.orderDetails.processing?.currentBar?.quantity || 1;
    };

    // Get the selected profile to check valid drainage options
    const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';
    // Normalize the profile type to handle both "56" and "N56" formats
    const normalizedProfile = profileType.startsWith('N') ? profileType : `N${profileType}`;
    const isValidProfileForDrainage = ['N6', 'N56', 'N106', 'N116'].includes(normalizedProfile);

    // Start at step 2 if in edit mode and drainage type is already selected
    const [step, setStep] = useState(() => {
        if (isEditMode && state.orderDetails.drainage && state.orderDetails.drainage.drainageType) {
            return 2;
        }
        return 1;
    });
    // Initialize state based on current order details if in edit mode
    const [drainageData, setDrainageData] = useState(() => {
        if (isEditMode && state.orderDetails.drainage) {
            return {
                needsDrainage: state.orderDetails.drainage.needsDrainage || null,
                drainageType: state.orderDetails.drainage.drainageType || null,
                stepType: state.orderDetails.stepType || null,
                customStepValue: state.orderDetails.customStepValue || null,
                action: null
            };
        }
        return {
            needsDrainage: null,
            drainageType: null,
            stepType: null,
            customStepValue: null,
            action: null,
            showFinishModal: false
        };
    });

    // Get the cart item if in edit mode
    const [cartItem, setCartItem] = useState(null);

    useEffect(() => {
        if (isEditMode && editItemIndex !== null && state.cartItems && state.cartItems[editItemIndex]) {
            setCartItem(state.cartItems[editItemIndex]);
        }
    }, [isEditMode, editItemIndex, state.cartItems]);

    const handleDrainageSelect = (drainageConfig) => {
        setDrainageData(prev => ({
            ...prev,
            needsDrainage: drainageConfig.needsDrainage,
            drainageType: drainageConfig.drainageType
        }));

        // If drainage is not needed, update the state but don't add a drainage article
        if (!drainageConfig.needsDrainage) {
            // Update the drainage process state to mark it as complete
            dispatch({
                type: 'UPDATE_ORDER',
                payload: {
                    drainage: {
                        needsDrainage: false,
                        drainageType: null,
                        isComplete: true
                    }
                }
            });

            // Complete the process without adding a drainage article
            if (onComplete) {
                onComplete({
                    needsDrainage: false,
                    drainageType: null,
                    stepType: null,
                    customStepValue: null,
                    action: 'finish'
                });
            }
            return;
        }

        // Move to step type selection
        setStep(2);
    };

    const handleStepTypeSelect = (stepConfig) => {
        // Prevent duplicate article additions
        if (articlesAddedRef.current) {
            console.log('Articles already added, skipping...');
            return;
        }

        const updatedData = {
            ...drainageData,
            stepType: stepConfig.stepType,
            customStepValue: stepConfig.customStepValue,
            action: stepConfig.action
        };

        setDrainageData(updatedData);

        // Mark that we're processing articles to prevent duplicates
        articlesAddedRef.current = true;

        // If going to progettazione (design phase) AND NOT fresatura-tappo, skip article creation
        // For fresatura-tappo, we need to show the tap finish modal first
        if ((stepConfig.action === 'progettazione' || stepConfig.customStepValue === 'progettazione')
            && updatedData.drainageType !== 'fresatura-tappo') {
            // Update the drainage process state
            dispatch({
                type: 'UPDATE_ORDER',
                payload: {
                    drainage: {
                        needsDrainage: updatedData.needsDrainage,
                        drainageType: updatedData.drainageType,
                        stepType: 'custom',
                        customStepValue: 'progettazione',
                        isComplete: true
                    },
                    stepType: 'custom',
                    customStepValue: 'progettazione'
                }
            });

            // Complete the process and go to progettazione
            if (onComplete) {
                onComplete({
                    ...updatedData,
                    action: 'progettazione'
                });
            }
            return;
        }

        // Generate article code based on drainage type and step type
        if (updatedData.needsDrainage && updatedData.drainageType) {
            const articleCode = generateArticleCode(updatedData.drainageType, updatedData.stepType);

            // Handle special case for foro-int-est (need to add both INT and EST)
            if (articleCode === 'FORO-INT-EST-COMBO') {
                let quantity = 1;
                if (isBarSupply) {
                    if (updatedData.stepType === 'standard') {
                        quantity = calcStandardHolesTotal();
                    } else if (updatedData.customStepValue && updatedData.customStepValue !== 'progettazione') {
                        quantity = calcCustomTotal(parseInt(updatedData.customStepValue, 10));
                    }
                }

                const suffix = quantity <= 40 ? '1' : '2';

                // Create LAV-INT9 article
                const intArticle = {
                    productCode: `LAV-INT9-${suffix}`,
                    description: `Scarico acqua - Foro Interno - ${getStepTypeLabel(updatedData.stepType, updatedData.customStepValue)}`,
                    quantity: quantity, // Number of holes
                    isDrainageArticle: true,
                    drainageType: 'foro-int',
                    stepType: updatedData.stepType,
                    customStepValue: updatedData.customStepValue,
                    isStandaloneArticle: true,
                    // Remove processing info from drainage articles to avoid confusion in display
                    processing: null
                };

                // Create LAV-EST9 article
                const estArticle = {
                    productCode: `LAV-EST9-${suffix}`,
                    description: `Scarico acqua - Foro Esterno - ${getStepTypeLabel(updatedData.stepType, updatedData.customStepValue)}`,
                    quantity: quantity, // Number of holes
                    isDrainageArticle: true,
                    drainageType: 'foro-est',
                    stepType: updatedData.stepType,
                    customStepValue: updatedData.customStepValue,
                    isStandaloneArticle: true,
                    // Remove processing info from drainage articles to avoid confusion in display
                    processing: null
                };

                // Add both articles to cart
                if (isEditMode && editItemIndex !== null) {
                    // In edit mode, we need to handle this differently
                    // For now, just update with the INT article
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: intArticle
                        }
                    });
                    // Add the EST as new
                    dispatch({
                        type: 'ADD_DRAINAGE_TO_CART',
                        payload: estArticle
                    });
                } else {
                    dispatch({
                        type: 'ADD_DRAINAGE_TO_CART',
                        payload: intArticle
                    });
                    dispatch({
                        type: 'ADD_DRAINAGE_TO_CART',
                        payload: estArticle
                    });
                }
            } else if (updatedData.drainageType === 'fresatura' || updatedData.drainageType === 'fresatura-tappo') {
                // Handle fresatura types - LAV40.5X20
                // If going to progettazione, don't create the fresatura article (quantity will be defined in progettazione)
                // BUT for fresatura-tappo, we still need to show the tap finish modal
                if (updatedData.customStepValue !== 'progettazione') {
                    let quantity = 1;
                    if (isBarSupply) {
                        if (updatedData.stepType === 'standard') {
                            quantity = calcStandardFresatureTotal();
                        } else if (updatedData.customStepValue && updatedData.customStepValue !== 'progettazione') {
                            quantity = calcCustomTotal(parseInt(updatedData.customStepValue, 10));
                        }
                    }

                    // Determine suffix based on quantity for LAV40.5X20
                    let suffix = '1';
                    if (quantity <= 40) suffix = '1';
                    else if (quantity >= 41 && quantity <= 150) suffix = '2';
                    else if (quantity > 150) suffix = '3';

                    const fresaturaArticle = {
                        productCode: `LAV40.5X20-${suffix}`,
                        description: `Scarico acqua - ${getDrainageTypeLabel(updatedData.drainageType)} - ${getStepTypeLabel(updatedData.stepType, updatedData.customStepValue)}`,
                        quantity: quantity, // Number of milling operations
                        isDrainageArticle: true,
                        drainageType: updatedData.drainageType,
                        stepType: updatedData.stepType,
                        customStepValue: updatedData.customStepValue,
                        isStandaloneArticle: true,
                        // Remove processing info from drainage articles to avoid confusion in display
                        processing: null
                    };

                    if (isEditMode && editItemIndex !== null) {
                        dispatch({
                            type: 'UPDATE_CART_ITEM',
                            payload: {
                                index: editItemIndex,
                                item: fresaturaArticle
                            }
                        });
                    } else {
                        dispatch({
                            type: 'ADD_DRAINAGE_TO_CART',
                            payload: fresaturaArticle
                        });
                    }
                }

                // If fresatura-tappo, show modal for N6-TF selection (ALWAYS, even for progettazione)
                if (updatedData.drainageType === 'fresatura-tappo') {
                    setDrainageData(prev => ({
                        ...prev,
                        showFinishModal: true
                    }));
                    return; // Early return to prevent calling onComplete
                }
            } else {
                // Standard single article (foro-int or foro-est)
                // Calculate total quantity: (holes per bar) × (number of bars)
                let singleQuantity = 1;
                if (isBarSupply) {
                    if (updatedData.stepType === 'standard') {
                        singleQuantity = calcStandardHolesTotal();
                    } else if (updatedData.customStepValue && updatedData.customStepValue !== 'progettazione') {
                        singleQuantity = calcCustomTotal(parseInt(updatedData.customStepValue, 10));
                    }
                }

                const drainageArticle = {
                    productCode: articleCode,
                    description: `Scarico acqua - ${getDrainageTypeLabel(updatedData.drainageType)} - ${getStepTypeLabel(updatedData.stepType, updatedData.customStepValue)}`,
                    quantity: singleQuantity, // Number of holes
                    isDrainageArticle: true,
                    drainageType: updatedData.drainageType,
                    stepType: updatedData.stepType,
                    customStepValue: updatedData.customStepValue,
                    isStandaloneArticle: true,
                    // Remove processing info from drainage articles to avoid confusion in display
                    processing: null
                };

                if (isEditMode && editItemIndex !== null) {
                    dispatch({
                        type: 'UPDATE_CART_ITEM',
                        payload: {
                            index: editItemIndex,
                            item: drainageArticle
                        }
                    });
                } else {
                    dispatch({
                        type: 'ADD_DRAINAGE_TO_CART',
                        payload: drainageArticle
                    });
                }
            }
        }

        // Notify parent component that the process is complete
        if (onComplete && !drainageData.showFinishModal) {
            onComplete(updatedData);
        }
    };

    // Handle finish selection for N6-TF-* product
    const handleFinishSelect = (finish) => {
        // Generate the N6-TF-* product code based on finish selection
        const finishSuffix = finish === 'RAL9005' ? 'N' : 'G'; // N for RAL9005, G for RAL9006
        const tfProductCode = `N6-TF-${finishSuffix}`;

        // Calculate the number of fresature to determine tap quantity (2 tappi per fresatura)
        let numberOfFresature = 1; // Default

        if (isBarSupply) {
            if (drainageData.stepType === 'standard') {
                numberOfFresature = calcStandardFresatureTotal();
            } else if (drainageData.customStepValue && drainageData.customStepValue !== 'progettazione') {
                numberOfFresature = calcCustomTotal(parseInt(drainageData.customStepValue, 10) || 1);
            } else {
                numberOfFresature = getTotalBars();
            }
        } else {
            numberOfFresature = state.orderDetails.quantity || 1;
        }

        const tapQuantity = numberOfFresature * 2; // 2 tappi per fresatura

        // Create the N6-TF-* article
        const tfArticle = {
            productCode: tfProductCode,
            description: `Tappo di finitura - ${finish === 'RAL9005' ? 'RAL9005 Opaco' : 'RAL9006 Opaco'}`,
            quantity: tapQuantity, // Bar quantity × 2
            isDrainageArticle: true,
            isAutomaticAddition: true,
            isStandaloneArticle: true,
            finish: finish
        };

        // Add the N6-TF-* article to cart
        dispatch({
            type: 'ADD_DRAINAGE_TO_CART',
            payload: tfArticle
        });

        // Close the finish selection modal
        setDrainageData(prev => ({
            ...prev,
            showFinishModal: false
        }));

        // Complete the drainage process
        if (onComplete) {
            // Pass the current drainageData with its action (which could be 'progettazione')
            onComplete({
                ...drainageData,
                finishSelected: finish
            });
        }
    };

    // Handle finish modal close without selection
    const handleFinishModalClose = () => {
        setDrainageData(prev => ({
            ...prev,
            showFinishModal: false
        }));

        // Complete the drainage process even without finish selection
        if (onComplete) {
            onComplete(drainageData);
        }
    };

    // Helper function to generate article code based on drainage type and step type
    const generateArticleCode = (drainageType, stepType) => {
        // For fresatura types
        if (drainageType === 'fresatura' || drainageType === 'fresatura-tappo') {
            return 'LAV40.5X20'; // Base code for fresatura
        }

        // For foro-int-est, we'll return a special marker
        if (drainageType === 'foro-int-est') {
            return 'FORO-INT-EST-COMBO'; // Special marker for combo
        }

        // Map drainage types to article codes for holes
        const drainageArticleCodes = {
            'foro-int': 'LAV-INT9',
            'foro-est': 'LAV-EST9'
        };

        const baseCode = drainageArticleCodes[drainageType];
        if (!baseCode) return 'DRAIN-UNKNOWN';

        let quantity = 1;
        if (isBarSupply) {
            if (stepType === 'standard') {
                quantity = calcStandardHolesTotal();
            } else if (stepType === 'custom' && drainageData.customStepValue && drainageData.customStepValue !== 'progettazione') {
                quantity = calcCustomTotal(parseInt(drainageData.customStepValue, 10));
            }
        }

        // Determine the suffix based on total quantity
        let suffix = '1';
        if (quantity <= 40) {
            suffix = '1';
        } else if (quantity >= 41) {
            suffix = '2';
        }

        return `${baseCode}-${suffix}`;
    };

    // Helper function to get a human-readable label for the drainage type
    const getDrainageTypeLabel = (drainageType) => {
        const drainageLabels = {
            'foro-int-est': 'Foro Interno-Esterno',
            'foro-int': 'Foro Interno',
            'foro-est': 'Foro Esterno',
            'fresatura': 'Fresatura',
            'fresatura-tappo': 'Fresatura con Tappo di Finitura'
        };

        return drainageLabels[drainageType] || 'Tipo sconosciuto';
    };

    // Helper function to get a human-readable label for the step type
    const getStepTypeLabel = (stepType, customStepValue) => {
        if (stepType === 'standard') {
            // For bar supply, add the standard holes info
            if (isBarSupply && currentBarLength) {
                const standardHoles = currentBarLength === 3 ? 2 : currentBarLength === 6 ? 3 : '';
                return `Passo Standard${standardHoles ? ` (${standardHoles} fori)` : ''}`;
            }
            return 'Passo Standard';
        } else if (stepType === 'custom' && customStepValue) {
            if (customStepValue === 'progettazione') {
                return 'Passo Personalizzato (Progettazione)';
            } else if (isBarSupply) {
                // For bar supply, show number of holes/milling
                const isMilling = drainageData.drainageType === 'fresatura' || drainageData.drainageType === 'fresatura-tappo';
                return `Passo Personalizzato (${customStepValue} ${isMilling ? 'fresature' : 'fori'})`;
            } else {
                return `Passo Personalizzato (${customStepValue} mm)`;
            }
        } else {
            return 'Passo Personalizzato';
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-4">
            <div className="bg-white rounded-lg shadow p-6">
                {step === 1 && (
                    <>
                        <h2 className="text-xl font-semibold text-center mb-6">
                            {isEditMode ? 'Modifica Scarico Acqua' : 'Configurazione Scarico Acqua'}
                        </h2>
                        <DrainageSelector
                            onSelect={handleDrainageSelect}
                            initialValues={{
                                drainageType: state.orderDetails.drainage?.drainageType || (cartItem?.drainageType)
                            }}
                            isEditMode={isEditMode}
                        />
                    </>
                )}

                {step === 2 && (
                    <>
                        <h2 className="text-xl font-semibold text-center mb-6">
                            {isEditMode ? 'Modifica Tipo di Passo' : 'Seleziona Tipo di Passo'}
                        </h2>

                        {/* Show bar supply info if applicable */}
                        {isBarSupply && (
                            <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="flex items-start">
                                    <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="text-sm text-red-900">
                                        {drainageData.drainageType === 'fresatura' || drainageData.drainageType === 'fresatura-tappo' ? (
                                            // FRESATURA information
                                            <>
                                                <p className="font-medium mb-1">Informazione sul passo per barre:</p>
                                                <ul className="list-disc ml-4 space-y-1">
                                                    <li>Fresature standard sono <strong>5 fresature</strong> a <strong>3 metri</strong></li>
                                                    <li>Per le barre <strong>6 metri</strong> sono <strong>10 fresature</strong></li>
                                                    <li>Partenza a <strong>200mm</strong>, interasse a <strong>600mm</strong></li>
                                                </ul>
                                                {currentBarLength && (
                                                    <p className="mt-2">
                                                        Lunghezza barra selezionata: <strong>{currentBarLength} metri</strong>
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            // FORATURA information (holes)
                                            <>
                                                <p className="font-medium mb-1">Informazione sul passo per barre:</p>
                                                <ul className="list-disc ml-4 space-y-1">
                                                    <li>Per barre da <strong>3 metri</strong>: il passo standard prevede <strong>2 fori</strong></li>
                                                    <li>Per barre da <strong>6 metri</strong>: il passo standard prevede <strong>3 fori</strong></li>
                                                </ul>
                                                {currentBarLength && (
                                                    <p className="mt-2">
                                                        Lunghezza barra selezionata: <strong>{currentBarLength} metri</strong>
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <StepTypeSelector
                            onSelect={handleStepTypeSelect}
                            initialValues={{
                                stepType: cartItem?.stepType || state.orderDetails.stepType,
                                customStepValue: cartItem?.customStepValue || state.orderDetails.customStepValue
                            }}
                            isBarSupply={isBarSupply}
                            barLength={currentBarLength}
                            drainageType={drainageData.drainageType}
                        />
                    </>
                )}

                {/* Progress Indicator */}
                <div className="mt-8 flex justify-center space-x-2">
                    {[1, 2].map((stepNumber) => (
                        <div
                            key={stepNumber}
                            className={`w-2 h-2 rounded-full ${stepNumber === step
                                ? 'bg-red-500'
                                : stepNumber < step
                                    ? 'bg-red-200'
                                    : 'bg-gray-200'
                                }`}
                        />
                    ))}
                </div>
            </div>

            {/* Finish Selection Modal */}
            {drainageData.showFinishModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">Seleziona Finitura per Tappo Fresatura</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Per la fresatura con tappo è necessario selezionare la finitura del tappo N6-TF-* (quantità x2)
                        </p>

                        <div className="space-y-3">
                            <button
                                onClick={() => handleFinishSelect('RAL9005')}
                                className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <div className="font-medium">RAL9005 Opaco</div>
                                <div className="text-sm text-gray-600">Codice: N6-TF-N</div>
                            </button>

                            <button
                                onClick={() => handleFinishSelect('RAL9006')}
                                className="w-full p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <div className="font-medium">RAL9006 Opaco</div>
                                <div className="text-sm text-gray-600">Codice: N6-TF-G</div>
                            </button>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={handleFinishModalClose}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Salta
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DrainageProcess;